import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { AccountingEnums } from "models/enums";
import { AccountingFormTypes } from "models/formTypes";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { ReportHanlder } from "models/propTypes/accountingPropTypes";
//#endregion
//#region components
import { DashboardButtonGroup, FilterButton, PageLayouts, StyledButton, ReloadEvent } from "components/common";
import { useQueryStateEnum } from "routing/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import StandardBalanceReport from "../balanceReport/standard";
import MonthlyBalanceReport from "../balanceReport/monthly";
import useFinancialReportFilterDefinition from "../forms/useReportFilterDefinition";
import ExpandAllFilterControl from "../expandAllFilterControl";
import { useFinancialReportService } from "serviceHooks";
//#endregion
import { EnumHelper, ExportHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

type BalanceReportTypes = Exclude<AccountingEnums.FinancialReportType, AccountingEnums.FinancialReportType.Tag>;

const BalanceReport: React.FC = (props) => {
  const financialReportService = useFinancialReportService();
  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const companyOrg = useSelector((store: AppState) => store.auth.selectedCompany.org ?? "");

  const reportRef = React.useRef<ReportHanlder>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [reportType, setReportType] = useQueryStateEnum<BalanceReportTypes>(
    "reportType",
    AccountingEnums.FinancialReportType.Standard
  );

  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.BalanceSheet);
  const reportTypeName = LocalizationHelperInstance.getFormattedMessage(`Accounting.FinancialReportType_${reportType}`);

  const [exportData, setExportData] = React.useState<any[][]>([]);

  const onReportTypeButtonClick = (type: BalanceReportTypes) => {
    location.search = "";
    navigate(location, { replace: true });
    setReportType(type);
  };

  const reportMap = {
    [AccountingEnums.FinancialReportType.Standard]: StandardBalanceReport,
    [AccountingEnums.FinancialReportType.Monthly]: MonthlyBalanceReport,
  };

  const ReportComponent = reportMap[reportType];

  const { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter, apiToReportMonthlyFilterFormatter } =
    useFinancialReportFilterDefinition(reportType, false);

  function exportReport(fileEnding: string) {
    const filename = `${title} - ${companyName} - ${reportTypeName}.${fileEnding}`;

    ExportHelper.exportReport(exportData, filename, title);

    return Promise.resolve();
  }

  function exportReportExcel() {
    return exportReport("xlsx");
  }

  function exportReportCSV() {
    return exportReport("csv");
  }

  const exportReportPDF = async () =>
    reportRef.current?.print(title, `${companyName} | ${companyOrg}`, `${title} - ${companyName} - ${reportTypeName}`);

  const getFilterProps = (reportType: AccountingEnums.FinancialReportType) => {
    switch (reportType) {
      case AccountingEnums.FinancialReportType.Standard:
        return {
          apiToReportFilterFormatter: apiToReportFilterFormatter,
          reportToAPIFilterFormatter: financialReportService.standardReportWithComparisonColumnsToAPIFilter,
          filterCategory: WintSuperkollAPIModelCustomFilters.FilterCategory.BalanceReport,
        };
      case AccountingEnums.FinancialReportType.Monthly:
        return {
          apiToReportFilterFormatter: apiToReportMonthlyFilterFormatter,
          reportToAPIFilterFormatter: financialReportService.monthlyReportToAPIFilter,
          filterCategory: WintSuperkollAPIModelCustomFilters.FilterCategory.BalanceReportMonthly,
        };
    }
  };

  return (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <PageLayouts.DashboardLayout<AccountingFormTypes.FinancialReportFilter>
          key={reportType}
          entityKey={0}
          title={title}
          topRightButtons={
            <DashboardButtonGroup
              buttons={(
                EnumHelper.getNumericItemValues(AccountingEnums.FinancialReportType)
                  .filter((r) => r !== AccountingEnums.FinancialReportType.Tag)
                  .map((i) => (
                    <StyledButton
                      size="sm"
                      onClick={onReportTypeButtonClick.bind(null, i)}
                      className={reportType !== i ? cssExports.transparent : ""}
                    >
                      {LocalizationHelperInstance.getFormattedMessage(`Accounting.FinancialReportType_${i}`)}
                    </StyledButton>
                  )) as (React.ReactElement | typeof ACTION_SEPARATOR)[]
              ).concat([ACTION_SEPARATOR, <FilterButton />, <ExpandAllFilterControl reportRef={reportRef} />])}
              moreActions={[
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToExcel),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportExcel,
                },
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportCSV,
                },
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DownloadAsPDF),
                  icon: IconDefinitionConstants.WDownload,
                  onClickHandler: exportReportPDF,
                  hidden: reportRef?.current == null,
                },
              ]}
            />
          }
          filterFields={filterFormFields}
          filterQueryStateHandler={filterQueryStateHandler}
          {...getFilterProps(reportType)}
        >
          <ReportComponent
            ref={reportRef}
            updateReportExportData={setExportData}
            setDisableExport={UtilsHelper.emptyFunc}
            reloadKey={reloadKey}
            reload={reload}
          />
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default BalanceReport;
